import customerServices from "../services/CustomerService";
import {
  CodApprovalStatusName,
  CodInvoice,
  CodOrder,
  CodOrderInvoices,
  CodOrderItem,
  CodPaymentTransaction,
  CodSelectedInvoice,
} from "../types/codSalesRep";

/** IQD money comparison slack — never use === on currency figures. */
export const COD_MONEY_EPS = 0.005;
/** A leftover below this can never be collected on a later visit. */
export const COD_MIN_REMAINDER = 1;

export const nearlyEqualMoney = (a: number, b: number, eps = COD_MONEY_EPS) =>
  Math.abs(Number(a || 0) - Number(b || 0)) <= eps;

export const unwrapListPayload = (data: unknown): Record<string, unknown>[] => {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== "object") return [];
  const raw = data as Record<string, unknown>;
  for (const key of [
    "items",
    "Items",
    "data",
    "Data",
    "results",
    "Results",
    "orders",
    "Orders",
  ]) {
    const value = raw[key];
    if (Array.isArray(value)) return value as Record<string, unknown>[];
  }
  return [];
};

export const parseSageOrderNumbers = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.map((value) => String(value ?? "").trim()).filter(Boolean);
  }
  const text = String(raw ?? "").trim();
  return text ? [text] : [];
};

export const approvalStatusName = (
  status?: number | string
): CodApprovalStatusName => {
  if (status === 1 || status === "1" || String(status).toLowerCase() === "pendingapproval") {
    return "PendingApproval";
  }
  if (status === 2 || status === "2" || String(status).toLowerCase() === "approved") {
    return "Approved";
  }
  if (status === 3 || status === "3" || String(status).toLowerCase() === "rejected") {
    return "Rejected";
  }
  return "None";
};

export const getOrderPayableTotal = (order: CodOrder): number => {
  const payable = Number(order.payableTotal ?? 0);
  if (payable > 0) return payable;
  return Number(order.amount ?? 0);
};

export const getOrderOutstanding = (order: CodOrder): number => {
  const explicit = Number(order.outstandingAmount);
  if (Number.isFinite(explicit) && explicit >= 0 && order.outstandingAmount != null) {
    return Math.max(0, explicit);
  }
  const paid = Number(order.amountPaid ?? 0);
  return Math.max(0, getOrderPayableTotal(order) - paid);
};

export const isFullyPaidCodOrder = (order: CodOrder): boolean => {
  const status = String(order.paymentStatus ?? "").toUpperCase();
  if (status === "PAID") return true;
  return getOrderOutstanding(order) <= COD_MONEY_EPS;
};

export const isPartiallyPaidCodOrder = (order: CodOrder): boolean => {
  const status = String(order.paymentStatus ?? "").toUpperCase().replace(/-/g, "_");
  if (status === "PARTIALLY_PAID" || status === "PARTIALLYPAID") return true;
  const paid = Number(order.amountPaid ?? 0);
  return paid > COD_MONEY_EPS && !isFullyPaidCodOrder(order);
};

export const isRejectedCodOrder = (order: CodOrder): boolean =>
  order.approvalStatus === 3 ||
  order.approvalStatusName === "Rejected" ||
  Boolean(order.rejectionReason);

export const isPostableCodOrder = (order: CodOrder): boolean =>
  (order.approvalStatus === 2 || order.approvalStatusName === "Approved") &&
  !isFullyPaidCodOrder(order);

export const validatePartialCollection = (
  remaining: number,
  entered: number
): "ok" | "mustPayFull" | "minRemainder" | "overBalance" | "invalid" => {
  if (!Number.isFinite(entered) || entered <= COD_MONEY_EPS) return "invalid";
  if (entered > remaining + COD_MONEY_EPS) return "overBalance";
  if (remaining <= COD_MIN_REMAINDER + COD_MONEY_EPS) {
    return nearlyEqualMoney(entered, remaining) ? "ok" : "mustPayFull";
  }
  if (
    !nearlyEqualMoney(entered, remaining) &&
    remaining - entered < COD_MIN_REMAINDER
  ) {
    return "minRemainder";
  }
  return "ok";
};

export const pickField = (
  raw: Record<string, unknown>,
  ...keys: string[]
): unknown => {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
};

export interface CustomerInfo {
  code: string;
  name: string;
}

export type CustomerLookup = Map<string, CustomerInfo>;

let customerLookupCache: CustomerLookup | null = null;

export const formatCodAmount = (amount: number, currency?: string) => {
  const prefix = currency?.trim() ? `${currency.trim()} ` : "IQD ";
  return `${prefix}${Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatCodDate = (
  value?: string,
  withTime = false
): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: withTime ? undefined : "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

export const computeItemTotal = (item: CodOrderItem) => {
  const gross = Number(item.grossPrice ?? 0);
  if (gross > 0) return gross;
  return Number(item.price ?? 0) * Number(item.quantity ?? 0);
};

export const computeOrderAmount = (
  raw: Record<string, unknown>,
  items: CodOrderItem[] = []
): number => {
  const total = Number(pickField(raw, "total", "Total") ?? 0);
  const subTotal = Number(pickField(raw, "subTotal", "SubTotal") ?? 0);
  const amount = Number(pickField(raw, "amount", "Amount") ?? 0);

  if (total > 0) return total;
  if (subTotal > 0) return subTotal;
  if (amount > 0) return amount;

  if (items.length > 0) {
    return items.reduce((sum, item) => sum + computeItemTotal(item), 0);
  }
  return 0;
};

export const buildCustomerLookup = async (): Promise<CustomerLookup> => {
  if (customerLookupCache) return customerLookupCache;
  customerLookupCache = new Map();
  return customerLookupCache;
};

/** Requests already in flight, shared so the same customer is never fetched twice at once. */
const customerFetchesInFlight = new Map<string, Promise<void>>();

/** Max parallel GET /customers/{id} calls (enough to be fast, few enough not to flood the API). */
const CUSTOMER_FETCH_CONCURRENCY = 6;

/**
 * Seed the lookup with customer code/name the API already sent on each record, so no network call
 * is needed for them. Never overwrites a richer entry.
 */
export const seedCustomerLookup = (
  lookup: CustomerLookup,
  rows: { customerId?: string; customerCode?: string; customerName?: string }[]
): CustomerLookup => {
  rows.forEach((row) => {
    const id = row.customerId?.trim();
    const code = row.customerCode?.trim() ?? "";
    const name = row.customerName?.trim() ?? "";
    if (!id || (!code && !name)) return;
    const existing = lookup.get(id);
    if (existing?.code && existing?.name) return;
    const info: CustomerInfo = { code: existing?.code || code, name: existing?.name || name };
    lookup.set(id, info);
    if (info.code && !lookup.has(info.code)) lookup.set(info.code, info);
  });
  return lookup;
};

const fetchOneCustomer = (id: string, lookup: CustomerLookup): Promise<void> => {
  const inFlight = customerFetchesInFlight.get(id);
  if (inFlight) return inFlight;
  const request = customerServices
    .getCustomerById(id)
    .then((res) => {
      const record = res?.data as Record<string, unknown> | undefined;
      if (!record) return;
      const recordId = String(pickField(record, "id", "Id") ?? "") || id;
      const info: CustomerInfo = {
        code: String(pickField(record, "customerCode", "CustomerCode") ?? ""),
        name: String(pickField(record, "customerName", "CustomerName") ?? ""),
      };
      lookup.set(recordId, info);
      if (info.code) lookup.set(info.code, info);
    })
    .catch(() => {
      /* display falls back to the code on the order */
    })
    .finally(() => {
      customerFetchesInFlight.delete(id);
    });
  customerFetchesInFlight.set(id, request);
  return request;
};

/**
 * Fetch customer code/name only for ids the lookup does not know yet — deduplicated, shared with
 * any concurrent caller, and run with a small concurrency pool instead of sequential batches.
 */
export const fetchCustomerInfoForIds = async (
  ids: string[],
  lookup: CustomerLookup
): Promise<CustomerLookup> => {
  const missing = Array.from(new Set(ids.filter((id) => id && !lookup.has(id))));
  if (missing.length === 0) return lookup;

  let next = 0;
  const worker = async () => {
    while (next < missing.length) {
      const id = missing[next++];
      if (!lookup.has(id)) await fetchOneCustomer(id, lookup);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CUSTOMER_FETCH_CONCURRENCY, missing.length) }, worker)
  );

  customerLookupCache = lookup;
  return lookup;
};

export const resolveCustomerDisplay = (
  order: CodOrder,
  lookup: CustomerLookup
): { customerCode: string; customerName: string } => {
  const fromLookup = lookup.get(order.customerId);
  let customerCode = order.customerCode?.trim() || fromLookup?.code || "";
  let customerName = order.customerName?.trim() || fromLookup?.name || "";

  // Title on orders is the SO number — never show it as customer.
  if (customerName === order.number) customerName = "";
  if (customerCode === order.number) customerCode = "";

  if (!customerName && customerCode) customerName = customerCode;
  if (!customerCode && customerName) customerCode = customerName;

  return { customerCode, customerName };
};

export const enrichCodOrder = (
  order: CodOrder,
  lookup: CustomerLookup
): CodOrder => {
  const items = (order.items ?? []).map((item) => ({
    ...item,
    grossPrice: computeItemTotal(item),
  }));
  const amount =
    Number(order.amount) > 0
      ? Number(order.amount)
      : items.reduce((sum, item) => sum + computeItemTotal(item), 0);
  const { customerCode, customerName } = resolveCustomerDisplay(order, lookup);

  return {
    ...order,
    items,
    amount,
    customerCode,
    customerName,
  };
};

export const enrichCodOrders = async (orders: CodOrder[]): Promise<CodOrder[]> => {
  const lookup = await buildCustomerLookup();
  // Orders already carry customerCode/customerName — only look up the ones that don't.
  seedCustomerLookup(lookup, orders);
  const customerIds = orders.map((o) => o.customerId).filter(Boolean);
  await fetchCustomerInfoForIds(customerIds, lookup);
  return orders.map((order) => enrichCodOrder(order, lookup));
};

export interface EnrichedCodPayment extends CodPaymentTransaction {
  orderNumber: string;
  customerName: string;
}

export const enrichCodPayments = (
  payments: CodPaymentTransaction[],
  orders: CodOrder[],
  lookup: CustomerLookup
): EnrichedCodPayment[] => {
  const orderById = new Map(orders.map((order) => [order.id, order]));

  return payments.map((payment) => {
    const order = orderById.get(payment.orderId);
    const fromLookup = lookup.get(payment.customerId);
    const fromOrder = order
      ? resolveCustomerDisplay(order, lookup)
      : { customerCode: "", customerName: "" };

    let customerCode =
      payment.customerCode?.trim() ||
      fromOrder.customerCode ||
      fromLookup?.code ||
      "";
    let customerName =
      fromOrder.customerName || fromLookup?.name || "";

    if (customerCode === payment.orderId) customerCode = "";
    if (customerName === payment.orderId) customerName = "";

    let amount = Number(payment.amount ?? 0);
    if (amount <= COD_MONEY_EPS) {
      if (order && isFullyPaidCodOrder(order)) {
        amount = Number(order.amount ?? payment.orderTotal ?? 0);
      } else if (Number(payment.orderTotal ?? 0) > COD_MONEY_EPS) {
        amount = Number(payment.orderTotal);
      } else if (order) {
        amount = Number(order.amountPaid ?? order.amount ?? 0);
      }
    }

    return {
      ...payment,
      amount,
      customerCode,
      customerName,
      orderNumber: order?.number || "",
    };
  });
};

export const codTableSx = {
  border: "1px solid rgba(226,232,240,0.5)",
  borderRadius: "12px",
  overflow: "hidden",
  "& .MuiTableCell-root": {
    padding: "12px 14px",
    borderBottom: "1px solid rgba(226,232,240,0.4)",
    fontSize: "13px",
  },
} as const;

export const codTableHeadCellSx = {
  fontWeight: 600,
  color: "#475569",
  fontSize: "12px !important",
  textTransform: "uppercase" as const,
  letterSpacing: "0.3px",
  background: "rgba(248,249,250,0.5)",
};

export const codSummaryCardSx = {
  p: 2,
  borderRadius: "12px",
  border: "1px solid rgba(226,232,240,0.5)",
  background: "rgba(255,255,255,0.8)",
} as const;

/** COD outstanding: not deleted, not rejected, not fully paid — includes pending-approval. */
export const isCodOrderOutstanding = (order: CodOrder): boolean => {
  if (order.isDeleted) return false;
  if (isFullyPaidCodOrder(order)) return false;
  if (isRejectedCodOrder(order)) return false;
  return true;
};

export interface CustomerOutstandingSummary {
  customerId: string;
  customerCode: string;
  customerName: string;
  unpaidOrderCount: number;
  outstandingAmount: number;
  currency: string;
}

/** Sum unpaid COD amounts per customer from the rep's order list. */
export const buildCustomerOutstandingLookup = (
  orders: CodOrder[]
): Map<string, number> => {
  const map = new Map<string, number>();
  orders.filter(isCodOrderOutstanding).forEach((order) => {
    const amount = getOrderOutstanding(order);
    if (order.customerId) {
      map.set(order.customerId, (map.get(order.customerId) ?? 0) + amount);
    }
    const code = order.customerCode?.trim().toLowerCase();
    if (code) {
      map.set(`code:${code}`, (map.get(`code:${code}`) ?? 0) + amount);
    }
  });
  return map;
};

export const resolveCustomerOutstanding = (
  lookup: Map<string, number>,
  customer: { id: string; customerCode?: string }
): number => {
  const byId = lookup.get(customer.id);
  if (byId != null && byId > 0) return byId;
  const code = customer.customerCode?.trim().toLowerCase();
  if (code) {
    const byCode = lookup.get(`code:${code}`);
    if (byCode != null) return byCode;
  }
  return 0;
};

export const buildCustomerOutstandingSummaries = (
  orders: CodOrder[]
): CustomerOutstandingSummary[] => {
  const map = new Map<string, CustomerOutstandingSummary>();

  orders.filter(isCodOrderOutstanding).forEach((order) => {
    const key = order.customerId || order.customerCode;
    if (!key) return;

    const existing = map.get(key) ?? {
      customerId: order.customerId,
      customerCode: order.customerCode ?? "",
      customerName: order.customerName ?? "",
      unpaidOrderCount: 0,
      outstandingAmount: 0,
      currency: order.currency ?? "IQD",
    };

    existing.unpaidOrderCount += 1;
    existing.outstandingAmount += getOrderOutstanding(order);
    if (order.customerName) existing.customerName = order.customerName;
    if (order.customerCode) existing.customerCode = order.customerCode;

    map.set(key, existing);
  });

  return Array.from(map.values()).sort(
    (a, b) => b.outstandingAmount - a.outstandingAmount
  );
};

/** Fill missing customerCode/customerName on API outstanding rows from rep orders + customer lookup. */
export const enrichOutstandingSummaries = (
  summaries: CustomerOutstandingSummary[],
  orders: CodOrder[],
  lookup: CustomerLookup = new Map()
): CustomerOutstandingSummary[] =>
  summaries.map((row) => {
    if (row.customerCode?.trim() && row.customerName?.trim()) return row;

    let customerCode = row.customerCode?.trim() ?? "";
    let customerName = row.customerName?.trim() ?? "";

    const customerOrders = orders.filter(
      (order) =>
        (row.customerId && order.customerId === row.customerId) ||
        (row.customerCode && order.customerCode === row.customerCode)
    );

    for (const order of customerOrders) {
      const display = resolveCustomerDisplay(order, lookup);
      customerCode = customerCode || display.customerCode;
      customerName = customerName || display.customerName;
      if (customerCode && customerName) break;
    }

    if (row.customerId) {
      const fromLookup = lookup.get(row.customerId);
      customerCode = customerCode || fromLookup?.code || "";
      customerName = customerName || fromLookup?.name || "";
    }

    if (!customerName && customerCode) customerName = customerCode;
    if (!customerCode && customerName) customerCode = customerName;

    return { ...row, customerCode, customerName };
  });

export const enrichOutstandingSummariesAsync = async (
  summaries: CustomerOutstandingSummary[],
  orders: CodOrder[]
): Promise<CustomerOutstandingSummary[]> => {
  const lookup = await buildCustomerLookup();
  seedCustomerLookup(lookup, summaries);
  seedCustomerLookup(lookup, orders);
  const customerIds = [
    ...summaries.map((row) => row.customerId),
    ...orders.map((order) => order.customerId),
  ].filter(Boolean);
  await fetchCustomerInfoForIds(customerIds, lookup);
  return enrichOutstandingSummaries(summaries, orders, lookup);
};

export interface OutstandingApiResult {
  currency: string;
  totalOutstandingAmount: number;
  totalUnpaidOrderCount: number;
  items: CustomerOutstandingSummary[];
}

export const normalizeOutstandingApiResponse = (
  data: unknown
): CustomerOutstandingSummary[] =>
  normalizeOutstandingApiPayload(data).items;

export const normalizeOutstandingApiPayload = (
  data: unknown
): OutstandingApiResult => {
  if (!data || typeof data !== "object") {
    return {
      currency: "IQD",
      totalOutstandingAmount: 0,
      totalUnpaidOrderCount: 0,
      items: [],
    };
  }
  const raw = data as Record<string, unknown>;
  const itemsRaw = pickField(raw, "items", "Items");
  const items = Array.isArray(itemsRaw)
    ? itemsRaw
        .map((row) => {
          const item = row as Record<string, unknown>;
          return {
            customerId: String(pickField(item, "customerId", "CustomerId") ?? ""),
            customerCode: String(pickField(item, "customerCode", "CustomerCode") ?? ""),
            customerName: String(pickField(item, "customerName", "CustomerName") ?? ""),
            unpaidOrderCount: Number(
              pickField(item, "unpaidOrderCount", "UnpaidOrderCount") ?? 0
            ),
            outstandingAmount: Number(
              pickField(item, "outstandingAmount", "OutstandingAmount") ?? 0
            ),
            currency: String(pickField(item, "currency", "Currency") ?? "IQD"),
          } satisfies CustomerOutstandingSummary;
        })
        .filter((row) => row.customerId || row.customerCode)
    : [];

  const totalOutstandingAmount = Number(
    pickField(raw, "totalOutstandingAmount", "TotalOutstandingAmount") ??
      items.reduce((sum, row) => sum + row.outstandingAmount, 0)
  );
  const totalUnpaidOrderCount = Number(
    pickField(raw, "totalUnpaidOrderCount", "TotalUnpaidOrderCount") ??
      items.reduce((sum, row) => sum + row.unpaidOrderCount, 0)
  );

  return {
    currency: String(pickField(raw, "currency", "Currency") ?? "IQD"),
    totalOutstandingAmount,
    totalUnpaidOrderCount,
    items,
  };
};

export const outstandingLookupFromSummaries = (
  summaries: CustomerOutstandingSummary[]
): Map<string, number> => {
  const map = new Map<string, number>();
  summaries.forEach((row) => {
    if (row.customerId) {
      map.set(row.customerId, row.outstandingAmount);
    }
    const code = row.customerCode?.trim().toLowerCase();
    if (code) {
      map.set(`code:${code}`, row.outstandingAmount);
    }
  });
  return map;
};

export const isSalesRepCodeConfigError = (message: string) =>
  /salesrepcode/i.test(message);

export const isCustomerNotAuthorizedError = (message: string) =>
  /not authorized to create orders/i.test(message);

export const isTradingCustomerError = (message: string) =>
  /not a trading customer/i.test(message);

export const isCustomerIdRequiredError = (message: string) =>
  /customerid is required/i.test(message);

export const isInvoiceNotIssuedError = (error: unknown) => {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response
    ?.data;
  const code = String(data?.error ?? data?.Error ?? "").toUpperCase();
  const message = String(data?.message ?? data?.Message ?? "");
  return code === "INVOICE_NOT_ISSUED" || /invoice_not_issued|has not issued an invoice/i.test(message);
};

export const isCodRouteMissingStatus = (status?: number) =>
  status === 404 || status === 405 || status === 501;

/* ── COD invoice-based payment ──────────────────────────────────────────────────
 * Payable amounts come from the API (invoice.remainingAmount, invoice.isPayable,
 * order.hasPayableInvoice). Nothing here is derived from the order total.
 */

/** Every payable invoice of the given orders, with the order/line context needed to show it. */
export const flattenPayableInvoices = (orders: CodOrderInvoices[]): CodSelectedInvoice[] =>
  orders.flatMap((order) =>
    order.lines.flatMap((line) =>
      line.invoices
        .filter((invoice) => invoice.isPayable)
        .map((invoice) => ({
          ...invoice,
          orderId: order.orderId,
          orderNumber: order.orderNumber,
          lineReference: line.reference,
          currency: order.currency,
        }))
    )
  );

/** Sum of the remaining amounts of the selected invoices (the amount a full collection pays). */
export const sumInvoiceRemaining = (invoices: Pick<CodInvoice, "remainingAmount">[]) =>
  invoices.reduce((sum, invoice) => sum + Number(invoice.remainingAmount || 0), 0);

/** Translation key for an invoice payment status (Pending | PartiallyPaid | Paid | Overdue). */
export const codInvoiceStatusKey = (status: string) => {
  const s = String(status ?? "").replace(/[\s_-]/g, "").toLowerCase();
  if (s === "partiallypaid") return "codSalesRep.invStatusPartiallyPaid";
  if (s === "paid") return "codSalesRep.invStatusPaid";
  if (s === "overdue") return "codSalesRep.invStatusOverdue";
  return "codSalesRep.invStatusPending";
};

/** StatusChip tone for an invoice payment status. */
export const codInvoiceStatusTone = (status: string) => {
  const s = String(status ?? "").replace(/[\s_-]/g, "").toLowerCase();
  if (s === "paid") return "paid";
  if (s === "partiallypaid") return "partlyPaid";
  if (s === "overdue") return "overdue";
  return "pending";
};

/** Text shown instead of a checkbox when an invoice cannot be paid. */
export const codNotPayableKey = (reason: string) => {
  const known = ["FULLY_PAID", "PAYMENT_IN_PROGRESS", "ORDER_NOT_APPROVED", "LEGACY_PAYMENT_UNRECONCILED"];
  const code = String(reason ?? "").trim().toUpperCase();
  return known.includes(code) ? `codSalesRep.notPayable${code}` : "";
};
